// Pre-save summary editor — review AI-extracted data before creating issue
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { CheckCircle, ArrowLeft } from 'lucide-react'
import { D365_VERSIONS, D365_MODULES, ISSUE_CATEGORIES, ISSUE_PRIORITIES } from '@/lib/constants'
import type { IssueRecorderOutput } from '@/types/ai'

interface IssueSummaryReviewProps {
  extractedData: IssueRecorderOutput['extractedData']
  onConfirm: (data: Record<string, string>) => void
  onBack: () => void
  isSaving: boolean
}

export function IssueSummaryReview({
  extractedData, onConfirm, onBack, isSaving,
}: IssueSummaryReviewProps): JSX.Element {
  const [title, setTitle] = useState(extractedData.problemTitle ?? '')
  const [description, setDescription] = useState(extractedData.problemDescription ?? '')
  const [version, setVersion] = useState(extractedData.d365Version ?? '')
  const [module, setModule] = useState(
    (extractedData.affectedModules as string[] | undefined)?.[0] ?? ''
  )
  const [category, setCategory] = useState('bug')
  const [priority, setPriority] = useState('medium')
  const [steps, setSteps] = useState(extractedData.stepsToReproduce ?? '')

  const handleConfirm = () => {
    onConfirm({
      title, description, d365Version: version, d365Module: module,
      category, priority, stepsToReproduce: steps,
    })
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-kaycoo-teal-dark">Review Issue Summary</h2>
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Chat
        </Button>
      </div>

      <p className="text-sm text-kaycoo-charcoal-2">
        Review and edit the AI-extracted issue details before saving.
      </p>

      <div className="space-y-3">
        <div>
          <Label htmlFor="title">Issue Title</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Brief issue title" />
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Detailed issue description" className="min-h-[100px]" />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>D365 Version</Label>
            <Select value={version} onValueChange={setVersion}>
              <SelectTrigger><SelectValue placeholder="Select version" /></SelectTrigger>
              <SelectContent>
                {D365_VERSIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Module</Label>
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
              <SelectContent>
                {D365_MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ISSUE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ISSUE_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="steps">Steps to Reproduce</Label>
          <Textarea id="steps" value={steps} onChange={(e) => setSteps(e.target.value)}
            placeholder="1. Navigate to...\n2. Click on...\n3. Error appears..." className="min-h-[80px]" />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          onClick={handleConfirm}
          disabled={isSaving || !title.trim()}
          className="flex-1 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
        >
          <CheckCircle className="mr-1.5 h-4 w-4" />
          {isSaving ? 'Saving...' : 'Confirm & Save Issue'}
        </Button>
      </div>
    </div>
  )
}
